/**
 * Chat content view displaying the header, messages list, and input composer.
 * Wraps the chat area with a drag-and-drop file zone.
 */
import type { ReactNode } from 'react'

import { ChatDropZoneOverlay } from '@/components/chat/chat-drop-zone-overlay.organism'

import type {
  ChatViewHandlers,
  ChatViewState,
  ChatViewTitleEditState,
  WebSocketState,
} from './chat.view.hook'
import { ConnectionStatusBar } from './chat-connection-status-bar.view'
import { getChatInputDisabledConfig } from './chat-content-input.helper'
import { buildInputSectionProps } from './chat-content-input-props.helper'
import { ChatHeaderSection } from './chat-header-section.view'
import { ChatInputSection } from './chat-input-section.view'
import { ChatMessagesSection } from './chat-messages-section.view'
import { ChatParticipantDialogs } from './chat-participant-dialogs.view'
import { useChatDropZone } from './use-chat-drop-zone.hook'
import type { UseChatParticipantActionsReturn } from './use-chat-participant-actions.hook'

interface ChatContentViewProps {
  state: ChatViewState
  handlers: ChatViewHandlers
  titleEditState: ChatViewTitleEditState
  participantActions: UseChatParticipantActionsReturn
  websocketState: WebSocketState
}

export const ChatContentView = ({
  state,
  handlers,
  titleEditState,
  participantActions,
  websocketState,
}: ChatContentViewProps): ReactNode => {
  const inputConfig = getChatInputDisabledConfig(state)
  const inputProps = buildInputSectionProps(state, handlers, inputConfig)
  const { isDragOver, dropZoneEventHandlerList } = useChatDropZone({
    onFilesSelected: handlers.handleFilesSelected,
  })

  return (
    <div
      className="chat-wrapper relative flex h-full min-h-0 flex-col"
      {...dropZoneEventHandlerList}
    >
      <ChatDropZoneOverlay isDragOver={isDragOver} />
      <ChatHeaderSection state={state} handlers={handlers} titleEditState={titleEditState} />
      <ChatMessagesSection state={state} handlers={handlers} />
      <ConnectionStatusBar websocketState={websocketState} />
      <ChatInputSection {...inputProps} />
      <ChatParticipantDialogs participantActions={participantActions} />
    </div>
  )
}
